import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, KeyRound, Ban, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", email: "", password: "", role: "buyer", supplier_id: "", department: "" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data.users)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data.suppliers)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { name: form.name, role: form.role, supplier_id: form.role === "supplier" ? form.supplier_id : null, department: form.department });
        toast.success("User updated");
      } else {
        await api.post("/users", { ...form, supplier_id: form.role === "supplier" ? form.supplier_id : null });
        toast.success("User created");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.post(`/users/${u.id}/${u.active === false ? "activate" : "deactivate"}`);
      toast.success(u.active === false ? "User activated" : "User deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, { password: resetPw });
      toast.success(`Password reset for ${resetFor.name}`);
      setResetFor(null);
      setResetPw("");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-users-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">User Management</h1>
          <p className="text-sm text-muted-foreground">{users.length} users · assign roles, link supplier accounts, deactivate access</p>
        </div>
        <Button data-testid="add-user-btn" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800"><Plus className="mr-1.5 h-4 w-4" /> Create User</Button>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Role</th>
              <th className="px-5 py-3 font-medium">Department / Supplier</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b last:border-0 hover:bg-slate-50">
                <td className="px-5 py-3 font-medium">{u.name}</td>
                <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-5 py-3"><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold capitalize">{u.role.replace("_", " ")}</span></td>
                <td className="px-5 py-3 text-muted-foreground">{u.role === "supplier" ? suppliers.find((s) => s.id === u.supplier_id)?.name || "—" : u.department || "—"}</td>
                <td className="px-5 py-3">
                  <span data-testid={`user-status-${u.email}`} className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${u.active === false ? "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]" : "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]"}`}>
                    {u.active === false ? "INACTIVE" : "ACTIVE"}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" data-testid={`user-edit-${u.email}`} onClick={() => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, supplier_id: u.supplier_id || "", department: u.department || "" }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`user-reset-${u.email}`} onClick={() => setResetFor(u)}><KeyRound className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`user-toggle-${u.email}`} onClick={() => toggleActive(u)} className={u.active === false ? "text-green-600" : "text-red-500"}>
                      {u.active === false ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">{editing ? `Edit ${editing.name}` : "Create User"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Name *</Label><Input data-testid="user-form-name" required className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            {!editing && <div><Label>Email *</Label><Input data-testid="user-form-email" type="email" required className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>}
            {!editing && <div><Label>Password *</Label><Input data-testid="user-form-password" required className="mt-1.5" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-form-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="buyer">Buyer</SelectItem>
                    <SelectItem value="procurement_manager">Procurement Manager</SelectItem>
                    <SelectItem value="org_admin">Admin</SelectItem>
                    <SelectItem value="supplier">Supplier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "supplier" ? (
                <div>
                  <Label>Linked Supplier *</Label>
                  <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                    <SelectTrigger data-testid="user-form-supplier" className="mt-1.5"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div><Label>Department</Label><Input data-testid="user-form-department" className="mt-1.5" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Procurement" /></div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="user-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Save Changes" : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={() => setResetFor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">Reset Password — {resetFor?.name}</DialogTitle></DialogHeader>
          <form onSubmit={resetPassword} className="space-y-4">
            <div><Label>New Password *</Label><Input data-testid="reset-password-input" required minLength={6} className="mt-1.5" value={resetPw} onChange={(e) => setResetPw(e.target.value)} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetFor(null)}>Cancel</Button>
              <Button type="submit" data-testid="reset-password-submit" className="bg-slate-900 hover:bg-slate-800">Reset Password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
